import { array, boolean, enum as enumFactory, literal, number, strictObject, string } from "../../lazy-zod.js";
                                  
import { openMeldRequestContextSchema } from "../daemon/profile-identity.js";
                                                        
import { messageChannelConversationLocatorSchema } from "../runtime/message-channel.js";
import { centerAgentVerifiedMemberToolSpeakerSchema } from "./center-agent-speaker.js";
import { spaceNameSchema } from "./space-name.js";
//#region src/schemas/http/center-agent-routines.ts
const CENTER_AGENT_ROUTINE_NAME_MAX_CHARS = 120;
const CENTER_AGENT_ROUTINE_INSTRUCTIONS_MAX_CHARS = 2e4;
const CENTER_AGENT_ROUTINE_CRON_EXPRESSION_MAX_CHARS = 256;
const CENTER_AGENT_ROUTINE_TIMEZONE_MAX_CHARS = 128;
const CENTER_AGENT_ROUTINE_SLACK_THREAD_LABEL_MAX_CHARS = 160;
const CENTER_AGENT_ROUTINE_RUN_FAILURE_DETAIL_MAX_CHARS = 4e3;
const CENTER_AGENT_ROUTINE_RUN_LIST_MAX_LIMIT = 100;
const CENTER_AGENT_ROUTINE_TARGET_SPACE_LIST_MAX_ITEMS = 500;
const CENTER_AGENT_ROUTINE_LIMIT = 50;
const CENTER_AGENT_ROUTINE_ERROR_CODES = [
	"routine.center_agent_required",
	"routine.client_update_required",
	"routine.forbidden",
	"routine.member_actor_required",
	"routine.not_found",
	"routine.schedule.invalid_cron",
	"routine.schedule.invalid_reference_time",
	"routine.schedule.invalid_timezone",
	"routine.schedule.too_frequent",
	"routine.schedule.run_at_past",
	"routine.status_conflict",
	"routine.store_unavailable",
	"routine.target_space_not_found",
	"routine.target_space_org_mismatch",
	"routine.target_space_publication_unavailable",
	"routine.slack_thread_unavailable",
	"routine.message_channel_unavailable",
	"routine.total_limit_reached"
];
const CENTER_AGENT_ROUTINE_STATUSES = [
	"enabled",
	"paused",
	"completed"
];
const CENTER_AGENT_ROUTINE_RUN_STATUSES = [
	"claimed",
	"submitted",
	"completed",
	"failed",
	"skipped"
];
const CENTER_AGENT_ROUTINE_TARGET_SPACE_KINDS = ["space", "center_agent_dm"];
const CENTER_AGENT_ROUTINE_DESTINATION_KINDS = [
	"space",
	"slack_thread",
	"message_channel"
];
const CENTER_AGENT_ROUTINE_AUTHORIZED_ACTIONS = ["organization_documents.save_approved_knowledge", "slack.publish_channel_report"];
const CENTER_AGENT_ROUTINE_EXTERNAL_ACTION_POLICIES = ["standard", "read_only"];
const CENTER_AGENT_ROUTINE_PRACTICAL_CONTRACT = "practical-v2";
const CENTER_AGENT_ROUTINE_MANAGEMENT_V1_CONTRACT = "management-v1";
const CENTER_AGENT_ROUTINE_MANAGEMENT_CONTRACT = "management-v2";
const trimmedIdentifierSchema = string().trim().min(1).max(256);
const timestampMsSchema = number().int().nonnegative();
const centerAgentRoutineRunScheduledForMsSchema = number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER);
const nullableTrimmedTextSchema = (maximum) => string().trim().min(1).max(maximum).nullable();
const queryIntegerPattern = /^\d+$/u;
const querySignedIntegerPattern = /^-?\d+$/u;
const centerAgentRoutineStatusSchema = enumFactory(CENTER_AGENT_ROUTINE_STATUSES);
const centerAgentRoutineRunStatusSchema = enumFactory(CENTER_AGENT_ROUTINE_RUN_STATUSES);
const centerAgentRoutineScheduleKindSchema = enumFactory(["cron", "once"]);
const centerAgentRoutineDestinationKindSchema = enumFactory(CENTER_AGENT_ROUTINE_DESTINATION_KINDS);
const centerAgentRoutineAuthorizedActionSchema = enumFactory(CENTER_AGENT_ROUTINE_AUTHORIZED_ACTIONS);
const centerAgentRoutineAuthorizedActionsSchema = array(centerAgentRoutineAuthorizedActionSchema).max(CENTER_AGENT_ROUTINE_AUTHORIZED_ACTIONS.length);
const centerAgentRoutineExternalActionPolicySchema = enumFactory(CENTER_AGENT_ROUTINE_EXTERNAL_ACTION_POLICIES);
const centerAgentRoutineContractSchema = literal(CENTER_AGENT_ROUTINE_PRACTICAL_CONTRACT);
const centerAgentRoutineListContractSchema = enumFactory([
	CENTER_AGENT_ROUTINE_PRACTICAL_CONTRACT,
	CENTER_AGENT_ROUTINE_MANAGEMENT_V1_CONTRACT,
	CENTER_AGENT_ROUTINE_MANAGEMENT_CONTRACT
]);
const centerAgentRoutineTargetSpaceKindSchema = enumFactory(CENTER_AGENT_ROUTINE_TARGET_SPACE_KINDS);
const centerAgentRoutineTargetSpaceSchema = strictObject({
	kind: centerAgentRoutineTargetSpaceKindSchema,
	name: spaceNameSchema,
	spaceId: trimmedIdentifierSchema
});
const centerAgentRoutineLastRunSummarySchema = strictObject({
	failureCode: nullableTrimmedTextSchema(256),
	failureDetail: nullableTrimmedTextSchema(CENTER_AGENT_ROUTINE_RUN_FAILURE_DETAIL_MAX_CHARS),
	finishedAtMs: timestampMsSchema.nullable(),
	id: trimmedIdentifierSchema,
	publishedSignalId: nullableTrimmedTextSchema(256),
	scheduledFor: centerAgentRoutineRunScheduledForMsSchema,
	startedAtMs: timestampMsSchema.nullable(),
	status: centerAgentRoutineRunStatusSchema
}).superRefine(validateRoutineRunScheduledFor);
const routineScheduleShape = {
	cronExpression: nullableTrimmedTextSchema(256),
	runAtMs: timestampMsSchema.nullable().default(null),
	scheduleKind: centerAgentRoutineScheduleKindSchema.default("cron"),
	timezone: nullableTrimmedTextSchema(128)
};
const routineDestinationShape = {
	messageChannelLocator: messageChannelConversationLocatorSchema.nullable().optional(),
	destinationKind: centerAgentRoutineDestinationKindSchema.default("space"),
	slackChannelId: nullableTrimmedTextSchema(256).default(null),
	slackConversationId: nullableTrimmedTextSchema(512).default(null),
	slackTeamId: nullableTrimmedTextSchema(256).default(null),
	slackThreadLabel: nullableTrimmedTextSchema(160).default(null),
	slackThreadTs: nullableTrimmedTextSchema(256).default(null),
	targetSpaceId: trimmedIdentifierSchema.nullable().default(null)
};
const centerAgentRoutineShape = {
	canManage: boolean(),
	createdAtMs: timestampMsSchema,
	createdByUserId: trimmedIdentifierSchema,
	...routineScheduleShape,
	...routineDestinationShape,
	id: trimmedIdentifierSchema,
	instructions: string().trim().min(1).max(CENTER_AGENT_ROUTINE_INSTRUCTIONS_MAX_CHARS),
	lastRun: centerAgentRoutineLastRunSummarySchema.nullable(),
	name: string().trim().min(1).max(120),
	nextDueAt: timestampMsSchema.nullable(),
	organizationId: trimmedIdentifierSchema,
	status: centerAgentRoutineStatusSchema,
	updatedAtMs: timestampMsSchema,
	updatedByUserId: trimmedIdentifierSchema
};
const centerAgentRoutineSchema = strictObject(centerAgentRoutineShape).superRefine(validateRoutineScheduleAndDestination);
const centerAgentRoutineCapabilityStateSchema = strictObject({
	approvedKnowledge: boolean(),
	canRepair: boolean(),
	externalActionPolicy: centerAgentRoutineExternalActionPolicySchema,
	slackChannelReport: boolean()
});
const centerAgentRoutineCreationInteractionSourceSchema = strictObject({
	client: string().trim().min(1).max(80),
	kind: literal("local_agent")
});
const centerAgentRoutineCreationAttributionSchema = strictObject({
	displayName: string().trim().min(1).max(64).nullable(),
	interactionSource: centerAgentRoutineCreationInteractionSourceSchema.optional()
});
const centerAgentRoutineManagementV1Shape = {
	...centerAgentRoutineShape,
	capabilityState: centerAgentRoutineCapabilityStateSchema
};
const centerAgentRoutineManagementV1Schema = strictObject(centerAgentRoutineManagementV1Shape).superRefine(validateRoutineScheduleAndDestination);
const centerAgentRoutineManagementSchema = strictObject({
	...centerAgentRoutineManagementV1Shape,
	creationAttribution: centerAgentRoutineCreationAttributionSchema
}).superRefine(validateRoutineScheduleAndDestination);
const centerAgentRoutineRunSchema = strictObject({
	...routineDestinationShape,
	failureCode: nullableTrimmedTextSchema(256),
	failureDetail: nullableTrimmedTextSchema(CENTER_AGENT_ROUTINE_RUN_FAILURE_DETAIL_MAX_CHARS),
	finishedAtMs: timestampMsSchema.nullable(),
	id: trimmedIdentifierSchema,
	nativeDeliveryId: nullableTrimmedTextSchema(256),
	organizationId: trimmedIdentifierSchema,
	publishedSignalId: nullableTrimmedTextSchema(256),
	routineId: trimmedIdentifierSchema,
	scheduledFor: centerAgentRoutineRunScheduledForMsSchema,
	startedAtMs: timestampMsSchema.nullable(),
	status: centerAgentRoutineRunStatusSchema
}).superRefine(validateRoutineRunScheduledFor);
const centerAgentRoutineLegacyV1Schema = strictObject({
	canManage: boolean(),
	createdAtMs: timestampMsSchema,
	createdByUserId: trimmedIdentifierSchema,
	cronExpression: string().trim().min(1).max(256),
	id: trimmedIdentifierSchema,
	instructions: string().trim().min(1).max(CENTER_AGENT_ROUTINE_INSTRUCTIONS_MAX_CHARS),
	lastRun: centerAgentRoutineLastRunSummarySchema.nullable(),
	name: string().trim().min(1).max(120),
	nextDueAt: timestampMsSchema.nullable(),
	organizationId: trimmedIdentifierSchema,
	scheduleKind: literal("cron"),
	status: enumFactory(["enabled", "paused"]),
	targetSpaceId: trimmedIdentifierSchema,
	timezone: string().trim().min(1).max(128),
	updatedAtMs: timestampMsSchema,
	updatedByUserId: trimmedIdentifierSchema
});
const centerAgentRoutineRunLegacyV1Schema = strictObject({
	failureCode: nullableTrimmedTextSchema(256),
	failureDetail: nullableTrimmedTextSchema(CENTER_AGENT_ROUTINE_RUN_FAILURE_DETAIL_MAX_CHARS),
	finishedAtMs: timestampMsSchema.nullable(),
	id: trimmedIdentifierSchema,
	nativeDeliveryId: nullableTrimmedTextSchema(256),
	organizationId: trimmedIdentifierSchema,
	publishedSignalId: nullableTrimmedTextSchema(256),
	routineId: trimmedIdentifierSchema,
	scheduledFor: centerAgentRoutineRunScheduledForMsSchema,
	startedAtMs: timestampMsSchema.nullable(),
	status: centerAgentRoutineRunStatusSchema
}).superRefine(validateRoutineRunScheduledFor);
const routineContractQueryShape = { contract: centerAgentRoutineContractSchema.optional() };
const centerAgentRoutineContractQuerySchema = strictObject(routineContractQueryShape);
const listCenterAgentRoutinesQuerySchema = strictObject({ contract: centerAgentRoutineListContractSchema.optional() });
const listCenterAgentRoutinesResponseSchema = strictObject({
	currentUserId: trimmedIdentifierSchema,
	organizationId: trimmedIdentifierSchema,
	routines: array(centerAgentRoutineSchema).max(50)
});
const listCenterAgentRoutinesManagementResponseSchema = strictObject({
	currentUserId: trimmedIdentifierSchema,
	organizationId: trimmedIdentifierSchema,
	routines: array(centerAgentRoutineManagementSchema).max(50)
});
const listCenterAgentRoutinesManagementV1ResponseSchema = strictObject({
	currentUserId: trimmedIdentifierSchema,
	organizationId: trimmedIdentifierSchema,
	routines: array(centerAgentRoutineManagementV1Schema).max(50)
});
const listCenterAgentRoutinesLegacyV1ResponseSchema = strictObject({
	currentUserId: trimmedIdentifierSchema,
	organizationId: trimmedIdentifierSchema,
	routines: array(centerAgentRoutineLegacyV1Schema).max(50)
});
const listCenterAgentRoutineTargetSpacesQuerySchema = strictObject({});
const listCenterAgentRoutineTargetSpacesResponseSchema = strictObject({
	organizationId: trimmedIdentifierSchema,
	spaces: array(centerAgentRoutineTargetSpaceSchema).max(500)
});
const createCenterAgentRoutineBodyShape = {
	authorizedActions: centerAgentRoutineAuthorizedActionsSchema.optional(),
	externalActionPolicy: centerAgentRoutineExternalActionPolicySchema.optional(),
	...routineScheduleShape,
	...routineDestinationShape,
	instructions: string().trim().min(1).max(CENTER_AGENT_ROUTINE_INSTRUCTIONS_MAX_CHARS),
	name: string().trim().min(1).max(120)
};
const createCenterAgentRoutineBodySchema = strictObject(createCenterAgentRoutineBodyShape).superRefine(validateRoutineScheduleAndDestination);
const createCenterAgentRoutineResponseSchema = strictObject({ routine: centerAgentRoutineSchema });
const centerAgentRoutineMutationLegacyV1ResponseSchema = strictObject({ routine: centerAgentRoutineLegacyV1Schema });
const centerAgentRoutineRouteParamsShape = { routineId: trimmedIdentifierSchema };
const centerAgentRoutineRouteParamsSchema = strictObject(centerAgentRoutineRouteParamsShape);
const routineUpdateShape = {
	messageChannelLocator: messageChannelConversationLocatorSchema.nullable().optional(),
	authorizedActions: centerAgentRoutineAuthorizedActionsSchema.optional(),
	externalActionPolicy: centerAgentRoutineExternalActionPolicySchema.optional(),
	cronExpression: string().trim().min(1).max(256).nullable().optional(),
	destinationKind: centerAgentRoutineDestinationKindSchema.optional(),
	instructions: string().trim().min(1).max(CENTER_AGENT_ROUTINE_INSTRUCTIONS_MAX_CHARS).optional(),
	name: string().trim().min(1).max(120).optional(),
	runAtMs: timestampMsSchema.nullable().optional(),
	scheduleKind: centerAgentRoutineScheduleKindSchema.optional(),
	slackChannelId: nullableTrimmedTextSchema(256).optional(),
	slackConversationId: nullableTrimmedTextSchema(512).optional(),
	slackTeamId: nullableTrimmedTextSchema(256).optional(),
	slackThreadLabel: nullableTrimmedTextSchema(160).optional(),
	slackThreadTs: nullableTrimmedTextSchema(256).optional(),
	targetSpaceId: trimmedIdentifierSchema.nullable().optional(),
	timezone: string().trim().min(1).max(128).nullable().optional()
};
const updateCenterAgentRoutineBodySchema = strictObject(routineUpdateShape).refine(hasRoutineUpdate, "At least one Routine field must be updated.");
const updateCenterAgentRoutineResponseSchema = strictObject({ routine: centerAgentRoutineSchema });
const pauseCenterAgentRoutineBodySchema = strictObject({});
const pauseCenterAgentRoutineResponseSchema = strictObject({ routine: centerAgentRoutineSchema });
const resumeCenterAgentRoutineBodySchema = strictObject({});
const resumeCenterAgentRoutineResponseSchema = strictObject({ routine: centerAgentRoutineSchema });
const deleteCenterAgentRoutineBodySchema = strictObject({});
const deleteCenterAgentRoutineResponseSchema = strictObject({
	deleted: literal(true),
	organizationId: trimmedIdentifierSchema,
	routineId: trimmedIdentifierSchema
});
const routineRunLimitSchema = number().int().min(1).max(100);
const queryScheduledForMsSchema = string().regex(querySignedIntegerPattern).transform((value) => Number(value)).pipe(centerAgentRoutineRunScheduledForMsSchema);
const queryLimitSchema = string().regex(queryIntegerPattern).transform((value) => Number(value)).pipe(routineRunLimitSchema);
const listCenterAgentRoutineRunsQuerySchema = strictObject({
	beforeScheduledFor: queryScheduledForMsSchema.optional(),
	...routineContractQueryShape,
	limit: queryLimitSchema.optional()
});
const listCenterAgentRoutineRunsResponseSchema = strictObject({
	organizationId: trimmedIdentifierSchema,
	routineId: trimmedIdentifierSchema,
	runs: array(centerAgentRoutineRunSchema).max(100)
});
const listCenterAgentRoutineRunsLegacyV1ResponseSchema = strictObject({
	organizationId: trimmedIdentifierSchema,
	routineId: trimmedIdentifierSchema,
	runs: array(centerAgentRoutineRunLegacyV1Schema).max(100)
});
const routineRpcContextShape = { context: openMeldRequestContextSchema.optional() };
const routineRpcContractShape = { contract: centerAgentRoutineContractSchema.optional() };
const listCenterAgentRoutinesRpcInputSchema = strictObject({
	...routineRpcContextShape,
	contract: centerAgentRoutineListContractSchema.optional()
});
const listCenterAgentRoutineTargetSpacesRpcInputSchema = strictObject(routineRpcContextShape);
const createCenterAgentRoutineRpcInputSchema = strictObject({
	...routineRpcContextShape,
	...routineRpcContractShape,
	...createCenterAgentRoutineBodyShape
}).superRefine(validateRoutineScheduleAndDestination);
const updateCenterAgentRoutineRpcInputSchema = strictObject({
	...routineRpcContextShape,
	...routineRpcContractShape,
	...centerAgentRoutineRouteParamsShape,
	...routineUpdateShape
}).refine(hasRoutineUpdate, "At least one Routine field must be updated.");
const pauseCenterAgentRoutineRpcInputSchema = strictObject({
	...routineRpcContextShape,
	...routineRpcContractShape,
	...centerAgentRoutineRouteParamsShape
});
const resumeCenterAgentRoutineRpcInputSchema = strictObject({
	...routineRpcContextShape,
	...routineRpcContractShape,
	...centerAgentRoutineRouteParamsShape
});
const deleteCenterAgentRoutineRpcInputSchema = strictObject({
	...routineRpcContextShape,
	...routineRpcContractShape,
	...centerAgentRoutineRouteParamsShape
});
const listCenterAgentRoutineRunsRpcInputSchema = strictObject({
	beforeScheduledFor: centerAgentRoutineRunScheduledForMsSchema.optional(),
	...routineRpcContextShape,
	...routineRpcContractShape,
	limit: routineRunLimitSchema.optional(),
	...centerAgentRoutineRouteParamsShape
});
const centerAgentRoutineToolSpeakerSchema = centerAgentVerifiedMemberToolSpeakerSchema;
const routineToolRpcContextShape = {
	context: openMeldRequestContextSchema,
	speakerMember: centerAgentRoutineToolSpeakerSchema
};
const centerAgentListRoutinesToolRpcInputSchema = strictObject(routineToolRpcContextShape);
const centerAgentCreateRoutineToolRpcInputSchema = strictObject({
	...routineToolRpcContextShape,
	...createCenterAgentRoutineBodyShape
}).superRefine(validateRoutineScheduleAndDestination);
const centerAgentUpdateRoutineToolRpcInputSchema = strictObject({
	...routineToolRpcContextShape,
	...centerAgentRoutineRouteParamsShape,
	...routineUpdateShape
}).refine(hasRoutineUpdate, "At least one Routine field must be updated.");
const centerAgentDeleteRoutineToolRpcInputSchema = strictObject({
	...routineToolRpcContextShape,
	...centerAgentRoutineRouteParamsShape
});
const centerAgentPauseRoutineToolRpcInputSchema = strictObject({
	...routineToolRpcContextShape,
	...centerAgentRoutineRouteParamsShape
});
const centerAgentResumeRoutineToolRpcInputSchema = strictObject({
	...routineToolRpcContextShape,
	...centerAgentRoutineRouteParamsShape
});
function hasRoutineUpdate(value) {
	return value.messageChannelLocator !== void 0 || value.authorizedActions !== void 0 || value.cronExpression !== void 0 || value.destinationKind !== void 0 || value.externalActionPolicy !== void 0 || value.instructions !== void 0 || value.name !== void 0 || value.runAtMs !== void 0 || value.scheduleKind !== void 0 || value.slackChannelId !== void 0 || value.slackConversationId !== void 0 || value.slackTeamId !== void 0 || value.slackThreadLabel !== void 0 || value.slackThreadTs !== void 0 || value.targetSpaceId !== void 0 || value.timezone !== void 0;
}
function validateRoutineScheduleAndDestination(value, context) {
	const recurringScheduleComplete = Boolean(value.cronExpression && value.timezone && value.runAtMs === null);
	const onceScheduleComplete = Boolean(value.runAtMs !== null && value.cronExpression === null && value.timezone === null);
	if (value.scheduleKind === "cron" && !recurringScheduleComplete || value.scheduleKind === "once" && !onceScheduleComplete) context.addIssue({
		code: "custom",
		message: "A recurring Routine requires cronExpression and timezone; a one-time Routine requires only runAtMs.",
		path: ["scheduleKind"]
	});
	const spaceDestinationComplete = Boolean(value.targetSpaceId && !value.messageChannelLocator && !value.slackTeamId && !value.slackChannelId && !value.slackThreadTs && !value.slackConversationId);
	const slackDestinationComplete = Boolean(!(value.targetSpaceId || value.messageChannelLocator) && value.slackTeamId && value.slackChannelId && value.slackThreadTs && value.slackConversationId);
	if (value.destinationKind === "space" && !spaceDestinationComplete || value.destinationKind === "slack_thread" && !slackDestinationComplete || value.destinationKind === "message_channel" && !(value.messageChannelLocator && value.messageChannelLocator.provider !== "slack" && !value.targetSpaceId && !value.slackTeamId && !value.slackChannelId && !value.slackThreadTs && !value.slackConversationId)) context.addIssue({
		code: "custom",
		message: "Choose a Space, a Slack thread, or a Feishu or Lark conversation with its complete destination.",
		path: ["destinationKind"]
	});
}
function validateRoutineRunScheduledFor(value, context) {
	if (value.scheduledFor >= 0 || value.status === "skipped" && value.failureCode === "routine.schedule_invalid") return;
	context.addIssue({
		code: "custom",
		message: "A negative Run scheduledFor is reserved for invalid-schedule quarantine evidence.",
		path: ["scheduledFor"]
	});
}
//#endregion
export { CENTER_AGENT_ROUTINE_AUTHORIZED_ACTIONS, CENTER_AGENT_ROUTINE_CRON_EXPRESSION_MAX_CHARS, CENTER_AGENT_ROUTINE_DESTINATION_KINDS, CENTER_AGENT_ROUTINE_ERROR_CODES, CENTER_AGENT_ROUTINE_EXTERNAL_ACTION_POLICIES, CENTER_AGENT_ROUTINE_INSTRUCTIONS_MAX_CHARS, CENTER_AGENT_ROUTINE_LIMIT, CENTER_AGENT_ROUTINE_MANAGEMENT_CONTRACT, CENTER_AGENT_ROUTINE_MANAGEMENT_V1_CONTRACT, CENTER_AGENT_ROUTINE_NAME_MAX_CHARS, CENTER_AGENT_ROUTINE_PRACTICAL_CONTRACT, CENTER_AGENT_ROUTINE_RUN_FAILURE_DETAIL_MAX_CHARS, CENTER_AGENT_ROUTINE_RUN_LIST_MAX_LIMIT, CENTER_AGENT_ROUTINE_RUN_STATUSES, CENTER_AGENT_ROUTINE_SLACK_THREAD_LABEL_MAX_CHARS, CENTER_AGENT_ROUTINE_STATUSES, CENTER_AGENT_ROUTINE_TARGET_SPACE_KINDS, CENTER_AGENT_ROUTINE_TARGET_SPACE_LIST_MAX_ITEMS, CENTER_AGENT_ROUTINE_TIMEZONE_MAX_CHARS, centerAgentCreateRoutineToolRpcInputSchema, centerAgentDeleteRoutineToolRpcInputSchema, centerAgentListRoutinesToolRpcInputSchema, centerAgentPauseRoutineToolRpcInputSchema, centerAgentResumeRoutineToolRpcInputSchema, centerAgentRoutineAuthorizedActionSchema, centerAgentRoutineAuthorizedActionsSchema, centerAgentRoutineCapabilityStateSchema, centerAgentRoutineContractQuerySchema, centerAgentRoutineContractSchema, centerAgentRoutineCreationAttributionSchema, centerAgentRoutineCreationInteractionSourceSchema, centerAgentRoutineDestinationKindSchema, centerAgentRoutineExternalActionPolicySchema, centerAgentRoutineLastRunSummarySchema, centerAgentRoutineLegacyV1Schema, centerAgentRoutineListContractSchema, centerAgentRoutineManagementSchema, centerAgentRoutineManagementV1Schema, centerAgentRoutineMutationLegacyV1ResponseSchema, centerAgentRoutineRouteParamsSchema, centerAgentRoutineRunLegacyV1Schema, centerAgentRoutineRunScheduledForMsSchema, centerAgentRoutineRunSchema, centerAgentRoutineRunStatusSchema, centerAgentRoutineScheduleKindSchema, centerAgentRoutineSchema, centerAgentRoutineStatusSchema, centerAgentRoutineTargetSpaceKindSchema, centerAgentRoutineTargetSpaceSchema, centerAgentRoutineToolSpeakerSchema, centerAgentUpdateRoutineToolRpcInputSchema, createCenterAgentRoutineBodySchema, createCenterAgentRoutineResponseSchema, createCenterAgentRoutineRpcInputSchema, deleteCenterAgentRoutineBodySchema, deleteCenterAgentRoutineResponseSchema, deleteCenterAgentRoutineRpcInputSchema, listCenterAgentRoutineRunsLegacyV1ResponseSchema, listCenterAgentRoutineRunsQuerySchema, listCenterAgentRoutineRunsResponseSchema, listCenterAgentRoutineRunsRpcInputSchema, listCenterAgentRoutineTargetSpacesQuerySchema, listCenterAgentRoutineTargetSpacesResponseSchema, listCenterAgentRoutineTargetSpacesRpcInputSchema, listCenterAgentRoutinesLegacyV1ResponseSchema, listCenterAgentRoutinesManagementResponseSchema, listCenterAgentRoutinesManagementV1ResponseSchema, listCenterAgentRoutinesQuerySchema, listCenterAgentRoutinesResponseSchema, listCenterAgentRoutinesRpcInputSchema, pauseCenterAgentRoutineBodySchema, pauseCenterAgentRoutineResponseSchema, pauseCenterAgentRoutineRpcInputSchema, resumeCenterAgentRoutineBodySchema, resumeCenterAgentRoutineResponseSchema, resumeCenterAgentRoutineRpcInputSchema, updateCenterAgentRoutineBodySchema, updateCenterAgentRoutineResponseSchema, updateCenterAgentRoutineRpcInputSchema };

//# sourceMappingURL=center-agent-routines.js.map